import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

export const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🔥'];

export function stableConversationId(listingId, uid, sellerId) {
  const sorted = [uid, sellerId].sort();
  return `${listingId}_${sorted[0]}_${sorted[1]}`;
}

/** Find or create a buyer↔seller thread for a listing (rules-safe: deterministic doc id). */
export async function getOrCreateConversation({ listingId, buyerUid, sellerId, listing }) {
  const convId = stableConversationId(listingId, buyerUid, sellerId);
  const ref = doc(db, 'conversations', convId);
  try {
    await setDoc(ref, {
      participantIds: [buyerUid, sellerId],
      listingId,
      listingTitle: listing?.title || '',
      listingPhoto: listing?.listingPhoto || listing?.photo || '',
      listingPrice: listing?.price || 0,
      lastMessage: '',
      typing: {},
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    // Another client may have created this thread first — still open it if readable.
    console.warn('[Sosello] conversation create skipped', err?.code || err);
  }
  return convId;
}

export function isConversationVisible(conversation, uid) {
  if (!conversation || !uid) return false;
  if (conversation.deletedForEveryone) return false;
  const hidden = conversation.hiddenFor;
  return !(Array.isArray(hidden) && hidden.includes(uid));
}

export async function markConversationUnread(conversationId, uid) {
  await updateDoc(doc(db, 'conversations', conversationId), {
    markedUnreadFor: arrayUnion(uid),
    [`unreadCounts.${uid}`]: 0,
  });
}

export async function deleteConversationForMe(conversationId, uid) {
  await updateDoc(doc(db, 'conversations', conversationId), {
    hiddenFor: arrayUnion(uid),
    [`unreadCounts.${uid}`]: 0,
    markedUnreadFor: arrayRemove(uid),
  });
}

export async function undoDeleteConversationForMe(conversationId, uid) {
  await updateDoc(doc(db, 'conversations', conversationId), {
    hiddenFor: arrayRemove(uid),
  });
}

export async function deleteConversationForEveryone(conversationId) {
  await updateDoc(doc(db, 'conversations', conversationId), {
    deletedForEveryone: true,
    deletedForEveryoneAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function undoDeleteConversationForEveryone(conversationId) {
  await updateDoc(doc(db, 'conversations', conversationId), {
    deletedForEveryone: false,
    deletedForEveryoneAt: deleteField(),
    updatedAt: serverTimestamp(),
  });
}

async function isLatestMessage(conversationId, messageId) {
  const snap = await getDocs(
    query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('createdAt', 'desc'),
      limit(1),
    ),
  );
  return snap.docs[0]?.id === messageId;
}

async function refreshConversationPreview(conversationId) {
  const snap = await getDocs(
    query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('createdAt', 'desc'),
      limit(30),
    ),
  );
  const preview =
    snap.docs
      .map((d) => d.data())
      .find((m) => m.deleted !== true && (m.text || '').trim())
      ?.text?.trim() || '';
  await updateDoc(doc(db, 'conversations', conversationId), {
    lastMessage: preview,
    updatedAt: serverTimestamp(),
  });
}

export async function editMessage(conversationId, messageId, text, uid) {
  const trimmed = (text || '').trim();
  if (!trimmed) return;
  const ref = doc(db, 'conversations', conversationId, 'messages', messageId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Message not found');
    const data = snap.data();
    if (data.senderId !== uid) throw new Error('Only your messages');
    if (data.deleted === true) throw new Error('Message deleted');
    tx.update(ref, {
      text: trimmed,
      edited: true,
      updatedAt: serverTimestamp(),
    });
  });
  if (await isLatestMessage(conversationId, messageId)) {
    await updateDoc(doc(db, 'conversations', conversationId), {
      lastMessage: trimmed,
      updatedAt: serverTimestamp(),
    });
  }
}

export async function softDeleteMessage(conversationId, messageId) {
  await updateDoc(doc(db, 'conversations', conversationId, 'messages', messageId), {
    deleted: true,
    text: '',
    deletedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await refreshConversationPreview(conversationId);
}

export async function toggleReaction(conversationId, messageId, emoji, uid) {
  const ref = doc(db, 'conversations', conversationId, 'messages', messageId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const reactions = { ...(snap.data().reactions || {}) };
    const wasOnThis = Array.isArray(reactions[emoji]) && reactions[emoji].includes(uid);
    // One reaction per user: clear this uid from every emoji first.
    for (const key of Object.keys(reactions)) {
      const list = [...(reactions[key] || [])].filter((id) => id !== uid);
      if (list.length) reactions[key] = list;
      else delete reactions[key];
    }
    if (!wasOnThis) {
      reactions[emoji] = [...(reactions[emoji] || []), uid];
    }
    tx.update(ref, { reactions });
  });
}

export function myReaction(message, uid) {
  if (!message?.reactions || !uid) return null;
  for (const [emoji, users] of Object.entries(message.reactions)) {
    if (Array.isArray(users) && users.includes(uid)) return emoji;
  }
  return null;
}

const TYPING_WINDOW_MS = 4000;

export function isOtherTyping(conversation, otherUid) {
  if (!conversation || !otherUid) return false;
  const raw = conversation.typing?.[otherUid];
  if (!raw) return false;
  const ms = typeof raw.toMillis === 'function' ? raw.toMillis() : Number(raw);
  if (!ms) return false;
  return Date.now() - ms < TYPING_WINDOW_MS;
}

export function messageReceipt(message, viewerUid, otherUid) {
  if (!message || message.senderId !== viewerUid || message.deleted) return null;
  const readBy = message.readBy || [];
  const deliveredTo = message.deliveredTo || [];
  if (otherUid && readBy.includes(otherUid)) return 'read';
  if (otherUid && deliveredTo.includes(otherUid)) return 'delivered';
  return 'sent';
}

export async function setTyping(conversationId, uid, typing) {
  await updateDoc(doc(db, 'conversations', conversationId), {
    [`typing.${uid}`]: typing ? Date.now() : deleteField(),
  });
}

export async function clearTyping(conversationId, uid) {
  await setTyping(conversationId, uid, false);
}

export async function clearUnread(conversationId, uid) {
  await updateDoc(doc(db, 'conversations', conversationId), {
    [`unreadCounts.${uid}`]: 0,
    markedUnreadFor: arrayRemove(uid),
  });
}

export async function acknowledgeMessages(conversationId, messages, uid, markRead = true) {
  if (!uid || !Array.isArray(messages)) return;
  const incoming = messages.filter((m) => m.senderId !== uid && !m.deleted);
  for (const msg of incoming) {
    const deliveredTo = msg.deliveredTo || [];
    const readBy = msg.readBy || [];
    const needsDelivered = !deliveredTo.includes(uid);
    const needsRead = markRead && !readBy.includes(uid);
    if (!needsDelivered && !needsRead) continue;
    const updates = {};
    if (needsDelivered) updates.deliveredTo = arrayUnion(uid);
    if (needsRead) updates.readBy = arrayUnion(uid);
    await updateDoc(doc(db, 'conversations', conversationId, 'messages', msg.id), updates);
  }
  if (markRead) await clearUnread(conversationId, uid);
}

export async function sendMessage(conversationId, text, uid, replyTo = null) {
  const trimmed = (text || '').trim();
  if (!trimmed) return;
  await clearTyping(conversationId, uid);
  const payload = {
    senderId: uid,
    text: trimmed,
    reactions: {},
    deleted: false,
    edited: false,
    deliveredTo: [uid],
    readBy: [uid],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (replyTo?.messageId) {
    payload.replyTo = {
      messageId: replyTo.messageId,
      text: String(replyTo.text || '').slice(0, 240),
      senderId: replyTo.senderId || '',
    };
  }
  await addDoc(collection(db, 'conversations', conversationId, 'messages'), payload);
}
